import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { getMaxTrainingPeriod } from 'webapp/utils/anomaly-detection';
import { copyObject } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSliderComponent from 'webapp/components/form/slider';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import SubmitButtonComponent from 'webapp/components/submit-button';

enum AnomalyConfigurationFormElementType {
	FormSliderComponent,
	Tabs,
}

export enum AnomalyConfigurationFormElementName {
	Sensitivity = 'anomalySensitivity',
	TrainingPeriod = 'anomalyTrainingPeriod',
	Adaptability = 'anomalyAdaptability',
}

export enum AdaptabilityValue {
	VERY_LOW = 'very-low',
	LOW = 'low',
	MEDIUM = 'medium',
	HIGH = 'high',
	VERY_HIGH = 'very_high',
}

interface FormElement {
	title: string;
	description: string;
	name: AnomalyConfigurationFormElementName;
	type: AnomalyConfigurationFormElementType;
	value: number | string;
	min?: number;
	max?: number;
	step?: number;
}

interface Adaptability {
	value: AdaptabilityValue;
	label: string;
}

export interface AnomalyConfiguration {
	[AnomalyConfigurationFormElementName.Sensitivity]: number;
	[AnomalyConfigurationFormElementName.TrainingPeriod]: number;
	[AnomalyConfigurationFormElementName.Adaptability]: AdaptabilityValue;
}

// the form values by name, cast to AnomalyConfiguration once all are set
type FormValues = Partial<Record<AnomalyConfigurationFormElementName, number | string>>;

const INITIAL_VALUES = [
	{
		title: 'Sensitivity',
		description:
			'Impacts the width of the expected range, where the metric is expected to perform. It is recommended to be lower than 0.2.',
		name: AnomalyConfigurationFormElementName.Sensitivity,
		type: AnomalyConfigurationFormElementType.FormSliderComponent,
		value: 0.2,
		min: 0.01,
		max: 0.5,
		step: 0.01,
	},
	{
		title: 'Extend training period',
		description:
			'Number of {granulation} before the selected date range to be used for training period.',
		name: AnomalyConfigurationFormElementName.TrainingPeriod,
		type: AnomalyConfigurationFormElementType.FormSliderComponent,
		value: 0,
		min: 0,
		max: 500,
		step: 1,
	},
	{
		title: 'Adaptability',
		description: 'Defines how much the model adjusts to new or evolving trends.',
		name: AnomalyConfigurationFormElementName.Adaptability,
		type: AnomalyConfigurationFormElementType.Tabs,
		value: AdaptabilityValue.MEDIUM,
	},
];

export default class MetricDetailsExploreModulesDataVisualizationToolbarAnomalyDetectionPopupConfigurationComponent extends PopupInnerContextComponent<PopupData.Anomaly.Configuration> {
	@service('popup') declare popupService: Service.Popup;
	@tracked formElements: FormElement[] = copyObject(INITIAL_VALUES);

	adaptabilityValues: Adaptability[] = [
		{
			value: AdaptabilityValue.LOW,
			label: 'Low',
		},
		{
			value: AdaptabilityValue.MEDIUM,
			label: 'Medium',
		},
		{
			value: AdaptabilityValue.HIGH,
			label: 'High',
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		next(() => {
			this.updateFormValues(this.popupData.anomalyConfiguration);
		});
	}

	get sliderType(): AnomalyConfigurationFormElementType {
		return AnomalyConfigurationFormElementType.FormSliderComponent;
	}

	get theme() {
		return this.args.model.theme;
	}

	@action
	handleFormElementChange(index: number, value: AnySimpleValue) {
		this.formElements[index]!.value = value as number;
	}

	@action
	onApply(): void {
		this.popupData.onApplyClick(
			this.formElements.reduce((acc: FormValues, element) => {
				acc[element.name] = element.value;
				return acc;
			}, {}) as AnomalyConfiguration,
		);
	}

	@action
	resetToDefaultValues() {
		const initValues = INITIAL_VALUES.reduce((acc: FormValues, element) => {
			acc[element.name] = element.value;
			return acc;
		}, {}) as AnomalyConfiguration;

		this.updateFormValues(initValues);
	}

	private updateFormValues(anomalyConfiguration: AnomalyConfiguration) {
		this.formElements = this.formElements.map((element) => {
			const value = anomalyConfiguration[element.name];

			if (
				element.name === AnomalyConfigurationFormElementName.TrainingPeriod &&
				this.popupData.granulationTypeId
			) {
				const maxTrainingPeriod = getMaxTrainingPeriod(this.popupData.granulationTypeId);
				element.max = maxTrainingPeriod.num;
				element.description = element.description.replace(
					'{granulation}',
					maxTrainingPeriod.text,
				);
			}

			return { ...element, value };
		});
	}
	<template>
		<div class="header">
			<a role="button" class="close-icon" {{on "click" this.close}}>
				<CommonUiIconComponent @name="Close" @size="sm" />
			</a>
		</div>
		<div class="body">
			<h5>Anomaly detection parameters</h5>

			{{#each this.formElements as |element index|}}
				<div class="form-element">
					<h6>{{element.title}}</h6>
					<p>{{element.description}}</p>
					{{#if (eq this.sliderType element.type)}}
						<FormSliderComponent
							@onChange={{fn this.handleFormElementChange index}}
							@value={{element.value}}
							@min={{element.min}}
							@max={{element.max}}
							@step={{element.step}}
							@name={{element.name}}
						/>
					{{else}}
						<FormRadiobuttonGroupComponent
							class="radiobutton-switcher"
							@name={{element.name}}
							@selected={{element.value}}
							@options={{this.adaptabilityValues}}
							@onChange={{fn this.handleFormElementChange index}}
							@theme={{this.theme}}
						/>
					{{/if}}
				</div>
			{{/each}}
		</div>

		<div class="footer">
			<SubmitButtonComponent @text="Apply" @noDelay={{true}} @onTrigger={{this.onApply}} />

			<a role="button" {{on "click" this.resetToDefaultValues}}>Reset to default</a>
		</div>
	</template>
}
